import { supabase } from './supabaseClient';
import { getCurrentCopy } from '../i18n/current';
import { teamService } from './teamService';
import { getProjectHolidays } from '../utils/taskBusinessRules';
import {
  absentBusinessDaysInColumn,
  allocationHoursInColumn,
  businessDaysBetween,
  capacityInColumn,
  getWeekColumns,
  rangeTouchesColumn,
  type PeriodColumn,
} from '../utils/periodColumns';
import { getProjectWorkWeekHours } from '../utils/duration';
import { parseDateFromDB } from '../utils/dateUtils';
import type {
  Resource,
  ResourceAllocation,
  ResourceAllocationWithJoins,
  ResourceAbsence,
  ColumnLoad,
  AllocationDraftImpact,
  SimulationTemplate,
  SimulationRole,
  WeeklyCapacityEntry,
  MonthlyCapacityEntry,
  PeriodSummary,
  ResourceCandidate,
  TeamMember,
  MemberWorkload,
  ReconciledUtilization,
  ReconciledStatus,
  ProjectSettings,
  EVMMetrics,
  Task,
} from '../types';

// ============================================================
// Utilitários de data
// ============================================================

function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function getWeeksBetween(startDate: string | Date, endDate: string | Date): { weekStart: Date; weekEnd: Date }[] {
  const weeks: { weekStart: Date; weekEnd: Date }[] = [];
  let current = getMonday(new Date(startDate));
  const end = new Date(endDate);

  while (current <= end) {
    const weekEnd = addDays(current, 6);
    weeks.push({
      weekStart: new Date(current),
      weekEnd: weekEnd > end ? new Date(end) : weekEnd
    });
    current = addDays(current, 7);
  }
  return weeks;
}

function getMonthsBetween(startDate: string | Date, endDate: string | Date): { month: string; monthStart: Date; monthEnd: Date }[] {
  const months: { month: string; monthStart: Date; monthEnd: Date }[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  let current = new Date(start.getFullYear(), start.getMonth(), 1);

  while (current <= end) {
    const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
    months.push({
      month: `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`,
      monthStart: new Date(current),
      monthEnd: monthEnd > end ? new Date(end) : monthEnd
    });
    current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
  }
  return months;
}

function rangesOverlap(aStart: string | Date, aEnd: string | Date, bStart: string | Date, bEnd: string | Date): boolean {
  return new Date(aStart) <= new Date(bEnd) && new Date(aEnd) >= new Date(bStart);
}

/**
 * Capacidade semanal da pessoa DENTRO deste projeto.
 *
 * Modelo do MS Project e do Primavera: o calendário do projeto define a jornada
 * padrão, o calendário do recurso define a disponibilidade da pessoa, e vale a
 * interseção dos dois. Ninguém entrega 40h num projeto que trabalha 24h por
 * semana, e um meio-período não vira integral porque o projeto é integral.
 *
 * Isto fecha a incoerência de origem do sistema: `weekly_capacity_hours` nasce
 * com default 40 no banco, e esse 40 nunca foi calculado como 5 × 8 em lugar
 * nenhum. A coincidência aritmética com o calendário padrão escondia o problema
 * até alguém marcar 6h/dia ou desmarcar a sexta, quando o cronograma se movia e
 * a capacidade continuava dizendo 40h.
 */
export function capacidadeSemanalEfetiva(
  resource: Pick<Resource, 'weekly_capacity_hours'>,
  projectSettings?: ProjectSettings | null
): number {
  const daPessoa = Math.max(resource.weekly_capacity_hours || 40, 0);
  if (!projectSettings) return daPessoa;
  return Math.min(daPessoa, getProjectWorkWeekHours(projectSettings));
}

// ============================================================
// Serviço de Capacidade
// ============================================================

export const capacityService = {

  // ---- RESOURCES (CRUD) ----

  async getResources(): Promise<Resource[]> {
    const { data, error } = await supabase
      .from('resources')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      if (error.message?.includes('AbortError')) return [];
      throw error;
    }
    return (data || []) as Resource[];
  },

  async createResource(resource: Partial<Resource>): Promise<Resource> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('resources')
      .insert([{
        user_id: user!.id,
        name: resource.name,
        email: resource.email || null,
        role_title: resource.role_title || null,
        avatar_color: resource.avatar_color || '#7e3eee',
        weekly_capacity_hours: resource.weekly_capacity_hours ?? 40,
        hourly_rate: resource.hourly_rate ?? 0,
        skills: resource.skills || [],
        is_active: resource.is_active !== false,
        linked_user_id: resource.linked_user_id || null,
        organization_id: resource.organization_id || null
      }])
      .select()
      .single();

    if (error) {
      console.error('[capacityService.createResource]', { resource, error });
      if (error.code === '23505') {
        const message = (error.message || '').toLowerCase();
        if (message.includes('linked_user') || message.includes('idx_resources_org_linked_user')) {
          throw new Error(getCurrentCopy().common.serviceErrors.resourceMemberTaken);
        }
        if (message.includes('email')) {
          throw new Error(getCurrentCopy().common.serviceErrors.resourceEmailTaken);
        }
      }
      throw new Error(error.message || error.details || 'Falha ao criar recurso');
    }
    return data as Resource;
  },

  async updateResource(id: string, updates: Partial<Resource>): Promise<Resource> {
    const ALLOWED_UPDATE_FIELDS: (keyof Resource)[] = [
      'name', 'email', 'role_title', 'avatar_color',
      'weekly_capacity_hours', 'hourly_rate', 'skills',
      'is_active', 'linked_user_id', 'organization_id'
    ];
    const payload: Record<string, unknown> = Object.fromEntries(
      Object.entries(updates).filter(([k]) => (ALLOWED_UPDATE_FIELDS as string[]).includes(k))
    );
    payload.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('resources')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('[capacityService.updateResource]', { id, payload, error });
      if (error.code === '23505') {
        const message = (error.message || '').toLowerCase();
        if (message.includes('linked_user') || message.includes('idx_resources_org_linked_user')) {
          throw new Error(getCurrentCopy().common.serviceErrors.resourceMemberTaken);
        }
        if (message.includes('email')) {
          throw new Error(getCurrentCopy().common.serviceErrors.resourceEmailTaken);
        }
      }
      throw new Error(error.message || error.details || 'Falha ao atualizar recurso');
    }
    return data as Resource;
  },

  async deleteResource(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('resources')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  async getResourcesByOrg(orgId: string): Promise<Resource[]> {
    const { data, error } = await supabase
      .from('resources')
      .select('*')
      .eq('organization_id', orgId)
      .order('name', { ascending: true });

    if (error) {
      if (error.message?.includes('AbortError')) return [];
      throw error;
    }
    return (data || []) as Resource[];
  },

  async syncOrgMembersToResources(orgId: string): Promise<number> {
    const { data, error } = await supabase.rpc('sync_all_org_members_to_resources', {
      p_org_id: orgId
    });

    if (error) throw error;
    return (data as number) || 0;
  },

  // ---- SYNC: RESOURCE → TEAM_MEMBER ----

  /**
   * Garante que um resource alocado a um projeto exista como team_member.
   * Evita duplicatas via resource_id ou fallback por nome.
   * @returns O team_member existente ou recém-criado.
   */
  async ensureTeamMember(resourceId: string, projectId: string): Promise<TeamMember | null> {

    // 1. Verificar se já existe team_member linkado via resource_id
    const existing = await teamService.getByProjectAndResource(projectId, resourceId);
    if (existing) return existing;

    // 2. Buscar o resource para obter dados
    const { data: resource, error: resErr } = await supabase
      .from('resources')
      .select('*')
      .eq('id', resourceId)
      .single();

    if (resErr || !resource) return null;

    // 3. Dedup pela identidade real: linked_user_id vence nome. Sem isso, um
    //    recurso com conta criava o quarto "Ricardo Bonizoli" do projeto.
    if ((resource as Resource).linked_user_id) {
      const { data: byUser } = await supabase
        .from('team_members')
        .select('*')
        .eq('project_id', projectId)
        .eq('linked_user_id', (resource as Resource).linked_user_id as string)
        .limit(1);
      if (byUser && byUser.length > 0) {
        const { data: updated } = await supabase
          .from('team_members')
          .update({ resource_id: resourceId })
          .eq('id', (byUser[0] as TeamMember).id)
          .select()
          .single();
        return (updated || byUser[0]) as TeamMember;
      }
    }

    // 4. Fallback: verificar por nome no projeto (pode ter sido criado manualmente).
    //    limit(1) em vez de maybeSingle(): com homônimos já duplicados, maybeSingle
    //    estoura erro de múltiplas linhas e a função criava MAIS uma duplicata.
    const { data: byNameRows } = await supabase
      .from('team_members')
      .select('*')
      .eq('project_id', projectId)
      .eq('name', (resource as Resource).name)
      .limit(1);
    const byName = byNameRows && byNameRows.length > 0 ? byNameRows[0] : null;

    if (byName) {
      // Linkar resource_id no team_member existente
      const { data: updated } = await supabase
        .from('team_members')
        .update({ resource_id: resourceId })
        .eq('id', (byName as TeamMember).id)
        .select()
        .single();
      return (updated || byName) as TeamMember;
    }

    // 4. Criar novo team_member com link ao resource
    const res = resource as Resource;
    const created = await teamService.create({
      project_id: projectId,
      name: res.name,
      email: res.email,
      role_title: res.role_title,
      avatar_color: res.avatar_color,
      resource_id: resourceId,
      linked_user_id: res.linked_user_id || null
    });

    return created;
  },

  // ---- SYNC: TEAM_MEMBER → RESOURCE ----

  /**
   * Garante que um team_member tenha um resource correspondente.
   * Busca por resource_id existente, depois por linked_user_id, depois cria novo.
   * Atualiza team_member.resource_id se necessário.
   * @returns O resource_id vinculado, ou null se falhar.
   */
  async ensureResource(teamMemberId: string): Promise<string | null> {
    // 1. Buscar team_member
    const { data: member, error: memErr } = await supabase
      .from('team_members')
      .select('*')
      .eq('id', teamMemberId)
      .single();

    if (memErr || !member) return null;
    const tm = member as TeamMember & { resource_id?: string | null; linked_user_id?: string | null };

    // 2. Se já tem resource_id → retornar
    if (tm.resource_id) return tm.resource_id;

    // 3. Se tem linked_user_id → buscar resource existente com esse linked_user_id
    if (tm.linked_user_id) {
      const { data: existingRes } = await supabase
        .from('resources')
        .select('id')
        .eq('linked_user_id', tm.linked_user_id)
        .limit(1)
        .maybeSingle();

      if (existingRes) {
        // Linkar e retornar
        await supabase
          .from('team_members')
          .update({ resource_id: (existingRes as { id: string }).id })
          .eq('id', teamMemberId);
        return (existingRes as { id: string }).id;
      }
    }

    // 4. Criar novo resource a partir dos dados do team_member
    try {
      const newResource = await this.createResource({
        name: tm.name,
        email: tm.email,
        role_title: tm.role_title,
        avatar_color: tm.avatar_color,
        linked_user_id: tm.linked_user_id || null,
      });

      // 5. Atualizar team_member.resource_id
      await supabase
        .from('team_members')
        .update({ resource_id: newResource.id })
        .eq('id', teamMemberId);

      return newResource.id;
    } catch (err) {
      console.error('[capacityService] ensureResource falhou ao criar resource:', err);
      return null;
    }
  },

  // ---- ALLOCATIONS (CRUD) ----

  async getAllocations(): Promise<ResourceAllocationWithJoins[]> {
    const { data, error } = await supabase
      .from('resource_allocations')
      .select('*, resources(name, role_title, avatar_color, weekly_capacity_hours), projects(name, status)')
      .eq('is_simulation', false)
      .order('start_date', { ascending: true });

    if (error) {
      if (error.message?.includes('AbortError')) return [];
      throw error;
    }
    // Filtrar alocações de projetos arquivados (completed/canceled) — não devem contar no Plano
    const active = (data || []).filter(a => {
      const status = (a as { projects?: { status?: string } }).projects?.status;
      return status !== 'completed' && status !== 'canceled';
    });
    return active as ResourceAllocationWithJoins[];
  },

  async getAllocationsByProject(projectId: string): Promise<ResourceAllocationWithJoins[]> {
    const { data, error } = await supabase
      .from('resource_allocations')
      .select('*, resources(name, role_title, avatar_color, weekly_capacity_hours)')
      .eq('project_id', projectId)
      .eq('is_simulation', false)
      .order('start_date', { ascending: true });

    if (error) {
      if (error.message?.includes('AbortError')) return [];
      throw error;
    }
    return (data || []) as ResourceAllocationWithJoins[];
  },

  async getAllocationsByResource(resourceId: string): Promise<ResourceAllocationWithJoins[]> {
    const { data, error } = await supabase
      .from('resource_allocations')
      .select('*, projects(name)')
      .eq('resource_id', resourceId)
      .eq('is_simulation', false)
      .order('start_date', { ascending: true });

    if (error) {
      if (error.message?.includes('AbortError')) return [];
      throw error;
    }
    return (data || []) as ResourceAllocationWithJoins[];
  },

  async createAllocation(allocation: Partial<ResourceAllocation>): Promise<ResourceAllocationWithJoins> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('resource_allocations')
      .insert([{
        user_id: user!.id,
        resource_id: allocation.resource_id,
        project_id: allocation.project_id,
        role_in_project: allocation.role_in_project || null,
        hours_per_week: allocation.hours_per_week || 0,
        start_date: allocation.start_date,
        end_date: allocation.end_date,
        is_simulation: allocation.is_simulation || false,
        notes: allocation.notes || null
      }])
      .select('*, resources(name, role_title, avatar_color, weekly_capacity_hours), projects(name)')
      .single();

    if (error) throw error;
    return data as ResourceAllocationWithJoins;
  },

  async updateAllocation(id: string, updates: Partial<ResourceAllocation>): Promise<ResourceAllocationWithJoins> {
    const { data, error } = await supabase
      .from('resource_allocations')
      .update(updates)
      .eq('id', id)
      .select('*, resources(name, role_title, avatar_color, weekly_capacity_hours), projects(name)')
      .single();

    if (error) throw error;
    return data as ResourceAllocationWithJoins;
  },

  async deleteAllocation(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('resource_allocations')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  async bulkCreateAllocations(allocations: Partial<ResourceAllocation>[]): Promise<ResourceAllocation[]> {
    const { data: { user } } = await supabase.auth.getUser();
    const rows = allocations.map(a => ({
      user_id: user!.id,
      resource_id: a.resource_id,
      project_id: a.project_id,
      role_in_project: a.role_in_project || null,
      hours_per_week: a.hours_per_week || 0,
      start_date: a.start_date,
      end_date: a.end_date,
      is_simulation: a.is_simulation || false,
      notes: a.notes || null
    }));

    const { data, error } = await supabase
      .from('resource_allocations')
      .insert(rows)
      .select();

    if (error) throw error;
    return (data || []) as ResourceAllocation[];
  },

  async deleteSimulationAllocations(): Promise<boolean> {
    const { error } = await supabase
      .from('resource_allocations')
      .delete()
      .eq('is_simulation', true);

    if (error) throw error;
    return true;
  },

  /**
   * Gera alocações iniciais para um projeto a partir do time e das tarefas.
   * Chamado por createProjectFromAIPlan, createProject, duplicateProject e o Simulador.
   *
   * Estratégia:
   *  - Para cada team_member, garante um resource_id via ensureResource().
   *  - Se `tasks` fornecido: soma estimated_hours das tasks atribuídas ao membro,
   *    dividido pelo nº de semanas do projeto → hours_per_week.
   *  - **Se o membro não tem tasks, NÃO cria alocação.** Ver nota abaixo.
   *  - Hours cap: 40h/semana (evita declarar mais que full-time sem intenção explícita).
   *
   * NOTA (16/08/2026, decisão do fundador): aqui existia um placeholder de 20h/semana
   * para todo membro sem tarefa. Ele virava plano de verdade no banco e inflava a
   * utilização do Portfólio com número que o usuário nunca digitou: um PMP abria o
   * trial e via percentual de gente que ele não alocou. Plano é afirmação, e afirmação
   * inventada custa a confiança de quem vive de PMBoK. Sem tarefa, sem alocação, e a
   * tela diz "sem plano" em texto.
   * Quem quiser o comportamento antigo passa `defaultHoursPerWeek` EXPLICITAMENTE
   * (intenção declarada, ex.: um simulador de cenário).
   *
   * Retorna as alocações criadas (pode ser vazio se não conseguir garantir resources).
   */
  async generateAllocationsFromProject(params: {
    projectId: string;
    teamMembers: Array<{ id: string; role_title?: string | null }>;
    projectStart: string;
    projectEnd: string;
    tasks?: Array<{ assigned: string | null; estimated_hours: number | null; duration: string | null }>;
    projectSettings?: ProjectSettings | null;
    hoursPerDay?: number;
    workDaysPerWeek?: number;
    defaultHoursPerWeek?: number;
    maxHoursPerWeek?: number;
  }): Promise<ResourceAllocation[]> {
    const {
      projectId,
      teamMembers,
      projectStart,
      projectEnd,
      tasks = [],
      projectSettings,
      hoursPerDay: hoursPerDayOverride,
      workDaysPerWeek: workDaysPerWeekOverride,
      defaultHoursPerWeek: defaultHoursOverride,
      maxHoursPerWeek: maxHoursOverride,
    } = params;

    if (!projectId || teamMembers.length === 0) return [];

    // Deriva calendário do projeto: explícito > settings > padrão 5 × 8
    const hoursPerDay = hoursPerDayOverride ?? projectSettings?.hoursPerDay ?? 8;
    const workDaysPerWeek = workDaysPerWeekOverride ?? projectSettings?.workDays?.length ?? 5;
    const projectWorkWeekHours = Math.max(1, workDaysPerWeek * hoursPerDay);
    // Cap padrão: min(40, calendário do projeto). Um projeto Mon/Wed/Fri × 10h cap em 30, não 40.
    const maxHoursPerWeek = maxHoursOverride ?? Math.min(40, projectWorkWeekHours);
    // Sem override, membro sem tarefa não vira alocação nenhuma (ver nota do método).
    const defaultHoursPerWeek = defaultHoursOverride ?? null;

    const { estimateHoursFromDuration, weeksBetween } = await import('../utils/duration');
    const weeks = weeksBetween(projectStart, projectEnd);

    const allocInputs: Partial<ResourceAllocation>[] = [];
    for (const member of teamMembers) {
      const resourceId = await this.ensureResource(member.id).catch(() => null);
      if (!resourceId) continue;

      const memberTasks = tasks.filter(t => t.assigned === member.id);
      const totalHours = memberTasks.reduce((sum, t) => {
        const fromField = typeof t.estimated_hours === 'number' && t.estimated_hours > 0 ? t.estimated_hours : null;
        return sum + (fromField ?? estimateHoursFromDuration(t.duration, hoursPerDay, workDaysPerWeek));
      }, 0);

      // Sem tarefa e sem default explícito: não há o que afirmar sobre esta pessoa.
      const derivedHoursPerWeek = totalHours > 0 ? totalHours / weeks : defaultHoursPerWeek;
      if (derivedHoursPerWeek === null) continue;
      const hoursPerWeek = Math.min(maxHoursPerWeek, Math.max(1, Math.round(derivedHoursPerWeek)));

      allocInputs.push({
        resource_id: resourceId,
        project_id: projectId,
        role_in_project: member.role_title || null,
        hours_per_week: hoursPerWeek,
        start_date: projectStart,
        end_date: projectEnd,
        is_simulation: false,
      });
    }

    if (allocInputs.length === 0) return [];
    try {
      return await this.bulkCreateAllocations(allocInputs);
    } catch (err) {
      console.error('[capacityService.generateAllocationsFromProject]', { projectId, err });
      return [];
    }
  },

  // ---- TEMPLATES (CRUD) ----

  async getTemplates(): Promise<SimulationTemplate[]> {
    const { data, error } = await supabase
      .from('simulation_templates')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      if (error.message?.includes('AbortError')) return [];
      throw error;
    }
    return (data || []) as SimulationTemplate[];
  },

  async createTemplate(template: Partial<SimulationTemplate>): Promise<SimulationTemplate> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('simulation_templates')
      .insert([{
        user_id: user!.id,
        name: template.name,
        description: template.description || null,
        roles: template.roles || []
      }])
      .select()
      .single();

    if (error) throw error;
    return data as SimulationTemplate;
  },

  async deleteTemplate(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('simulation_templates')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  // ============================================================
  // CÁLCULOS (funções puras, client-side)
  // ============================================================

  /**
   * Calcula capacidade semanal de um recurso em um período.
   * @returns Array<{ weekStart, weekEnd, capacity, committed, free, utilization, overbooking }>
   */
  calculateWeeklyCapacity(resource: Resource, allocations: ResourceAllocation[], startDate: string | Date, endDate: string | Date): WeeklyCapacityEntry[] {
    const weeks = getWeeksBetween(startDate, endDate);
    const weeklyCapacity: number = Math.max(resource.weekly_capacity_hours || 40, 0.1);

    return weeks.map(({ weekStart, weekEnd }) => {
      const committed: number = allocations
        .filter(a => !a.is_simulation && rangesOverlap(a.start_date, a.end_date, weekStart, weekEnd))
        .reduce((sum: number, a) => sum + (parseFloat(String(a.hours_per_week)) || 0), 0);

      const free: number = Math.max(0, weeklyCapacity - committed);
      const utilization: number = (committed / weeklyCapacity) * 100;
      const overbooking: number = Math.max(0, committed - weeklyCapacity);

      return {
        weekStart: formatDate(weekStart),
        weekEnd: formatDate(weekEnd),
        capacity: weeklyCapacity,
        committed: Math.round(committed * 10) / 10,
        free: Math.round(free * 10) / 10,
        utilization: Math.round(utilization * 10) / 10,
        overbooking: Math.round(overbooking * 10) / 10
      };
    });
  },

  /**
   * Calcula capacidade mensal de um recurso em um período.
   * @returns Array<{ month, capacity, committed, free, utilization }>
   */
  calculateMonthlyCapacity(
    resource: Resource,
    allocations: ResourceAllocation[],
    startDate: string | Date,
    endDate: string | Date,
    projectSettings?: ProjectSettings | null
  ): MonthlyCapacityEntry[] {
    const months = getMonthsBetween(startDate, endDate);
    const workDays = (projectSettings?.workDays && projectSettings.workDays.length > 0)
      ? projectSettings.workDays
      : [1, 2, 3, 4, 5];
    const workDaysPerWeek = Math.max(1, workDays.length);
    const holidays = getProjectHolidays(projectSettings);
    const weeklyCapacity: number = Math.max(capacidadeSemanalEfetiva(resource, projectSettings), 0.1);
    const dailyCapacity: number = weeklyCapacity / workDaysPerWeek;

    return months.map(({ month, monthStart, monthEnd }) => {
      const bizDays: number = businessDaysBetween(monthStart, monthEnd, workDays, holidays);
      const monthCapacity: number = dailyCapacity * bizDays;

      const committed: number = allocations
        .filter(a => !a.is_simulation && rangesOverlap(a.start_date, a.end_date, monthStart, monthEnd))
        .reduce((sum: number, a) => {
          // parseDateFromDB e não new Date(): a string ISO pura vira meia-noite
          // UTC, que em UTC-3 é 21h do dia ANTERIOR. Misturada num Math.max com
          // `monthStart` (meia-noite local), a data escolhida era normalizada
          // depois por `atMidnight` para o dia errado, e a sobreposição contava
          // um dia útil a mais.
          const inicioAloc = parseDateFromDB(a.start_date) ?? monthStart;
          const fimAloc = parseDateFromDB(a.end_date) ?? monthEnd;
          const overlapStart = new Date(Math.max(inicioAloc.getTime(), monthStart.getTime()));
          const overlapEnd = new Date(Math.min(fimAloc.getTime(), monthEnd.getTime()));
          const overlapBizDays: number = businessDaysBetween(overlapStart, overlapEnd, workDays, holidays);
          const dailyAlloc: number = (parseFloat(String(a.hours_per_week)) || 0) / workDaysPerWeek;
          return sum + (dailyAlloc * overlapBizDays);
        }, 0);

      const free: number = Math.max(0, monthCapacity - committed);
      const utilization: number = monthCapacity > 0 ? (committed / monthCapacity) * 100 : 0;

      return {
        month,
        capacity: Math.round(monthCapacity * 10) / 10,
        committed: Math.round(committed * 10) / 10,
        free: Math.round(free * 10) / 10,
        utilization: Math.round(utilization * 10) / 10
      };
    });
  },

  /**
   * Calcula resumo de capacidade para um recurso em um período.
   * @returns { capacity, committed, free, utilization, overbooking, peakWeek }
   */
  calculatePeriodSummary(resource: Resource, allocations: ResourceAllocation[], startDate: string | Date, endDate: string | Date): PeriodSummary {
    const weeks = this.calculateWeeklyCapacity(resource, allocations, startDate, endDate);
    if (weeks.length === 0) {
      return { capacity: 0, committed: 0, free: 0, utilization: 0, overbooking: 0, peakWeek: null };
    }

    const totalCapacity: number = weeks.reduce((s, w) => s + w.capacity, 0);
    const totalCommitted: number = weeks.reduce((s, w) => s + w.committed, 0);
    const totalFree: number = Math.max(0, totalCapacity - totalCommitted);
    const utilization: number = totalCapacity > 0 ? (totalCommitted / totalCapacity) * 100 : 0;
    const totalOverbooking: number = weeks.reduce((s, w) => s + w.overbooking, 0);
    const peakWeek: WeeklyCapacityEntry | null = weeks.reduce<WeeklyCapacityEntry | null>((max, w) => w.utilization > (max?.utilization || 0) ? w : max, null);

    return {
      capacity: Math.round(totalCapacity * 10) / 10,
      committed: Math.round(totalCommitted * 10) / 10,
      free: Math.round(totalFree * 10) / 10,
      utilization: Math.round(utilization * 10) / 10,
      overbooking: Math.round(totalOverbooking * 10) / 10,
      peakWeek
    };
  },

  // ============================================================
  // Equipe & Alocação: split aqui/outros, agregado da janela e
  // validação de rascunho (handoff 14/08/2026)
  // ============================================================

  /**
   * Carga de UM recurso por coluna da grade, separando as horas DESTE projeto
   * das horas de outros projetos, com pro-rata por dias úteis de interseção e
   * capacidade zerada nas colunas tocadas por ausência.
   */
  splitCommittedByColumn(
    resource: Resource,
    allAllocations: ResourceAllocation[],
    projectId: string,
    columns: PeriodColumn[],
    absences: ResourceAbsence[],
    projectSettings?: ProjectSettings | null
  ): ColumnLoad[] {
    const mine = allAllocations.filter(a => a.resource_id === resource.id && !a.is_simulation);
    const myAbsences = absences.filter(ab => ab.resource_id === resource.id);
    const workDays = projectSettings?.workDays?.length ? projectSettings.workDays : undefined;
    const holidays = getProjectHolidays(projectSettings);
    const weekly = capacidadeSemanalEfetiva(resource, projectSettings);

    return columns.map(col => {
      const absent = myAbsences.some(ab => rangeTouchesColumn(ab, col));
      const diasAusentes = absentBusinessDaysInColumn(myAbsences, col, workDays, holidays);
      let here = 0;
      let other = 0;
      for (const a of mine) {
        const h = allocationHoursInColumn(a, col, workDays, holidays);
        if (!h) continue;
        if (a.project_id === projectId) here += h;
        else other += h;
      }
      return {
        key: col.key,
        here: Math.round(here * 10) / 10,
        other: Math.round(other * 10) / 10,
        cap: capacityInColumn(weekly, col, workDays, holidays, diasAusentes),
        absent,
      };
    });
  },

  /**
   * Agregado do time na janela (KPI "Capacidade real" e linha "Total do time").
   * `committed` soma apenas as horas DESTE projeto; `other` fica de fora de
   * propósito: é contexto por célula, não demanda do projeto.
   */
  aggregateTeamWindow(perMember: ColumnLoad[][]): {
    committed: number;
    capacity: number;
    byColumn: { key: string; committed: number; capacity: number }[];
  } {
    const byColumn: { key: string; committed: number; capacity: number }[] = [];
    perMember.forEach(cols => {
      cols.forEach((c, i) => {
        if (!byColumn[i]) byColumn[i] = { key: c.key, committed: 0, capacity: 0 };
        byColumn[i].committed = Math.round((byColumn[i].committed + c.here) * 10) / 10;
        byColumn[i].capacity = Math.round((byColumn[i].capacity + c.cap) * 10) / 10;
      });
    });
    const committed = Math.round(byColumn.reduce((s, c) => s + c.committed, 0) * 10) / 10;
    const capacity = Math.round(byColumn.reduce((s, c) => s + c.capacity, 0) * 10) / 10;
    return { committed, capacity, byColumn };
  },

  /**
   * Impacto de um rascunho de alocação ANTES de salvar (aviso ao vivo do
   * formulário). Considera todas as alocações da pessoa (todos os projetos) e
   * as ausências (semana ausente tem capacidade zero). Não monta mensagem:
   * devolve números e o front traduz via i18n.
   */
  validateAllocationDraft(
    resource: Resource,
    allAllocationsOfResource: ResourceAllocation[],
    absences: ResourceAbsence[],
    draft: { hours_per_week: number; start_date: string; end_date: string },
    projectSettings?: ProjectSettings | null
  ): AllocationDraftImpact {
    const workDays = projectSettings?.workDays?.length ? projectSettings.workDays : undefined;
    const holidays = getProjectHolidays(projectSettings);
    const weekly = capacidadeSemanalEfetiva(resource, projectSettings);
    const empty: AllocationDraftImpact = {
      fits: true,
      projectedPeakHours: 0,
      capacity: weekly,
      peakWeekStart: null,
      suggestedHours: weekly,
    };
    if (!draft.start_date || !draft.end_date) return empty;
    if (new Date(draft.end_date) < new Date(draft.start_date)) return empty;

    const weeks = getWeekColumns(draft.start_date, draft.end_date);
    if (weeks.length === 0) return empty;

    const mine = allAllocationsOfResource.filter(a => a.resource_id === resource.id && !a.is_simulation);
    const myAbsences = absences.filter(ab => ab.resource_id === resource.id);
    const draftRange = {
      start_date: draft.start_date,
      end_date: draft.end_date,
      hours_per_week: draft.hours_per_week,
    };

    let fits = true;
    let peak = 0;
    let peakWeekStart: string | null = null;
    let worstOver = -Infinity;
    let suggested = weekly;

    for (const w of weeks) {
      // Mesma regra da grade: ausência desconta os dias ausentes, não a semana.
      const diasAusentes = absentBusinessDaysInColumn(myAbsences, w, workDays, holidays);
      const cap = capacityInColumn(weekly, w, workDays, holidays, diasAusentes);
      const existing = mine.reduce((s, a) => s + allocationHoursInColumn(a, w, workDays, holidays), 0);
      const draftHours = allocationHoursInColumn(draftRange, w, workDays, holidays);
      const projected = Math.round((existing + draftHours) * 10) / 10;

      if (projected > cap) fits = false;
      const over = projected - cap;
      if (over > worstOver || (over === worstOver && projected > peak)) {
        worstOver = over;
        peak = projected;
        // Formata em horário LOCAL: toISOString() deslocaria o dia fora de UTC.
        peakWeekStart = `${w.start.getFullYear()}-${String(w.start.getMonth() + 1).padStart(2, '0')}-${String(w.start.getDate()).padStart(2, '0')}`;
      }
      const room = Math.max(0, cap - existing);
      if (room < suggested) suggested = room;
    }

    return {
      fits,
      projectedPeakHours: peak,
      capacity: weekly,
      peakWeekStart,
      suggestedHours: Math.floor(suggested),
    };
  },

  /**
   * Encontra recursos disponíveis para um papel em um período.
   * Usado pelo simulador para sugestão de time.
   * @returns Array<{ resource, availableHoursPerWeek, fitScore, verdict, overflowWeeks }>
   */
  findAvailableResources(resources: Resource[], allAllocations: ResourceAllocation[], neededRole: string, startDate: string | Date, endDate: string | Date, hoursPerWeekNeeded: number): ResourceCandidate[] {
    const activeResources: Resource[] = resources.filter(r => r.is_active !== false);

    const candidates: ResourceCandidate[] = activeResources
      .filter(r => {
        if (!neededRole) return true;
        const role = (r.role_title || '').toLowerCase();
        const skills = (r.skills || []).map(s => s.toLowerCase());
        const search = neededRole.toLowerCase();
        return role.includes(search) || skills.some(s => s.includes(search));
      })
      .map(resource => {
        const resourceAllocations = allAllocations.filter(a => a.resource_id === resource.id);
        const weeks = this.calculateWeeklyCapacity(resource, resourceAllocations, startDate, endDate);

        if (weeks.length === 0) {
          return { resource, availableHoursPerWeek: resource.weekly_capacity_hours || 40, fitScore: 100, verdict: 'green' as const, overflowWeeks: 0 };
        }

        const availablePerWeek: number[] = weeks.map(w => w.free);
        const avgAvailable: number = availablePerWeek.reduce((s, v) => s + v, 0) / availablePerWeek.length;
        const minAvailable: number = Math.min(...availablePerWeek);
        const overflowWeeks: number = weeks.filter(w => w.free < hoursPerWeekNeeded).length;

        let fitScore: number = hoursPerWeekNeeded > 0
          ? (avgAvailable / hoursPerWeekNeeded) * 100
          : 100;

        // Penalizar semanas com overflow
        fitScore -= overflowWeeks * 10;
        fitScore = Math.max(0, Math.min(100, fitScore));

        let verdict: 'green' | 'yellow' | 'red' = 'red';
        if (fitScore >= 80 && overflowWeeks === 0) verdict = 'green';
        else if (fitScore >= 40 || overflowWeeks <= Math.ceil(weeks.length * 0.2)) verdict = 'yellow';

        return {
          resource,
          availableHoursPerWeek: Math.round(avgAvailable * 10) / 10,
          minAvailable: Math.round(minAvailable * 10) / 10,
          fitScore: Math.round(fitScore),
          verdict,
          overflowWeeks
        };
      });

    candidates.sort((a, b) => b.fitScore - a.fitScore || a.overflowWeeks - b.overflowWeeks);
    return candidates;
  },

  /**
   * Retorna a cor do status baseada na utilização.
   */
  getUtilizationColor(utilization: number): 'red' | 'yellow' | 'green' {
    if (utilization >= 90) return 'red';
    if (utilization >= 70) return 'yellow';
    return 'green';
  },

  /**
   * Retorna o label do status baseado na utilização.
   */
  /** Escada de limiares de utilização. Devolve CÓDIGO, não texto de tela. */
  getUtilizationStatus(utilization: number): ReconciledStatus {
    if (utilization >= 100) return 'overbooking';
    if (utilization >= 90) return 'critical';
    if (utilization >= 70) return 'attention';
    return 'available';
  },

  /**
   * Reconcilia as duas camadas de capacidade (Plano = allocations, Execução = tasks)
   * numa única visão para UI. Fonte única de verdade para cards, pickers e badges.
   *
   * Parâmetros:
   *  - resource: recurso alvo
   *  - allocations: TODAS as alocações (a função filtra por resource.id internamente)
   *  - taskWorkload: workload do recurso no período; null/undefined se indisponível
   *  - dateRange: período considerado (Portfolio usa mês/trimestre; pickers usam o mesmo)
   *
   * Labels de divergência (usados no lugar do genérico "Sobrecarregado"):
   *   - "Sem plano formal" → tem tarefas, mas não há alocação (caso comum dos bugs reportados)
   *   - "Plano ocioso"     → alocação existe mas execução muito abaixo (>20% de gap)
   *   - "Execução acima do plano" → execução excede o plano em margem relevante (>20%)
   *   - Caso contrário, usa os limiares tradicionais (Overbooking/Crítico/Atenção/Disponível).
   */
  computeReconciledUtilization(
    resource: Resource,
    allocations: ResourceAllocation[],
    taskWorkload: MemberWorkload | null | undefined,
    dateRange: { startDate: string | Date; endDate: string | Date }
  ): ReconciledUtilization {
    const ownAllocations = allocations.filter(a => a.resource_id === resource.id && !a.is_simulation);
    const summary = this.calculatePeriodSummary(resource, ownAllocations, dateRange.startDate, dateRange.endDate);
    const planUtil = summary.utilization;
    const execUtil = Math.max(0, taskWorkload?.utilization ?? 0);
    const effectiveUtil = Math.max(planUtil, execUtil);

    const DIVERGENCE_THRESHOLD = 20; // pontos percentuais
    const hasAnyExec = execUtil > 0;
    const hasAnyPlan = planUtil > 0;

    let divergence: ReconciledUtilization['divergence'] = 'none';
    if (hasAnyExec && !hasAnyPlan) divergence = 'no-plan';
    else if (hasAnyPlan && !hasAnyExec && planUtil < 10) divergence = 'idle-plan';
    else if (hasAnyPlan && execUtil > planUtil + DIVERGENCE_THRESHOLD) divergence = 'exec-over-plan';
    else if (hasAnyPlan && planUtil > execUtil + DIVERGENCE_THRESHOLD && planUtil < 70) divergence = 'idle-plan';

    let status: ReconciledUtilization['status'];
    let badge: ReconciledUtilization['badge'];
    if (divergence === 'no-plan' && execUtil >= 70) {
      status = 'no-formal-plan';
      badge = execUtil >= 100 ? 'danger' : 'warning';
    } else if (divergence === 'exec-over-plan') {
      status = 'exec-over-plan';
      badge = execUtil >= 100 ? 'danger' : 'warning';
    } else if (divergence === 'idle-plan') {
      status = 'idle-plan';
      badge = 'info';
    } else {
      status = this.getUtilizationStatus(effectiveUtil);
      badge = status === 'overbooking' || status === 'critical' ? 'danger'
        : status === 'attention' ? 'warning'
        : 'success';
    }

    return {
      planUtil: Math.round(planUtil * 10) / 10,
      execUtil: Math.round(execUtil * 10) / 10,
      effectiveUtil: Math.round(effectiveUtil * 10) / 10,
      status,
      badge,
      divergence,
    };
  },

  /**
   * Calcula métricas de Earned Value Management (PMBoK / MS Project).
   *
   * Fórmulas:
   *   PV  = Σ(baseline_estimated_hours × rate)   — Planned Value
   *   EV  = Σ(estimated_hours × progress/100 × rate) — Earned Value
   *   AC  = Σ(actual_hours × rate)                — Actual Cost
   *   SV  = EV − PV                                — Schedule Variance (negativo = atrasado)
   *   CV  = EV − AC                                — Cost Variance (negativo = acima do orçamento)
   *   SPI = EV / PV                                — Schedule Performance Index (<1 = atrasado)
   *   CPI = EV / AC                                — Cost Performance Index (<1 = caro)
   *   BAC = Σ(baseline_estimated_hours × rate)    — Budget at Completion (igual ao PV total quando baseline = escopo completo)
   *   EAC = BAC / CPI                              — Estimate at Completion
   *
   * Rate por task é derivado do responsável primário (task.assigned → team_member → resource.hourly_rate).
   * Tasks sem rate conhecido contribuem 0 (PM deve definir `hourly_rate` no ResourceManager para ter EVM completo).
   * Milestones (type='milestone') e grupos (type='group') são ignorados.
   */
  computeEVMMetrics(
    tasks: Task[] | Array<Pick<Task, 'type' | 'estimated_hours' | 'actual_hours' | 'progress' | 'baseline_estimated_hours' | 'assigned'>>,
    teamMembers: TeamMember[],
    resources: Resource[]
  ): EVMMetrics {
    const memberToResource = new Map<string, Resource>();
    teamMembers.forEach(tm => {
      if (tm.resource_id) {
        const res = resources.find(r => r.id === tm.resource_id);
        if (res) memberToResource.set(tm.id, res);
      }
    });

    let PV = 0, EV = 0, AC = 0, BAC = 0;

    for (const t of tasks) {
      if (t.type === 'milestone' || t.type === 'group') continue;
      const rate = t.assigned ? (memberToResource.get(t.assigned)?.hourly_rate ?? 0) : 0;
      const baselineHours = typeof t.baseline_estimated_hours === 'number' ? t.baseline_estimated_hours : 0;
      const estHours = typeof t.estimated_hours === 'number' ? t.estimated_hours : 0;
      const actHours = typeof t.actual_hours === 'number' ? t.actual_hours : 0;
      const progressRatio = Math.min(1, Math.max(0, (t.progress || 0) / 100));

      PV += baselineHours * rate;
      EV += estHours * progressRatio * rate;
      AC += actHours * rate;
      BAC += baselineHours * rate;
    }

    const SV = EV - PV;
    const CV = EV - AC;
    const SPI = PV > 0 ? EV / PV : 0;
    const CPI = AC > 0 ? EV / AC : 0;
    const EAC = CPI > 0 ? BAC / CPI : BAC;

    const round = (v: number) => Math.round(v * 100) / 100;
    return {
      PV: round(PV),
      EV: round(EV),
      AC: round(AC),
      SV: round(SV),
      CV: round(CV),
      SPI: round(SPI),
      CPI: round(CPI),
      BAC: round(BAC),
      EAC: round(EAC),
    };
  },

  /**
   * Deriva horas restantes de uma task.
   * - Se `remaining_hours` tem valor explícito → respeita (estilo Jira override).
   * - Caso contrário → max(0, estimated_hours - actual_hours).
   */
  deriveRemainingHours(task: Pick<Task, 'estimated_hours' | 'actual_hours' | 'remaining_hours'>): number {
    if (typeof task.remaining_hours === 'number' && task.remaining_hours >= 0) {
      return task.remaining_hours;
    }
    const est = typeof task.estimated_hours === 'number' ? task.estimated_hours : 0;
    const act = typeof task.actual_hours === 'number' ? task.actual_hours : 0;
    return Math.max(0, est - act);
  },

  /**
   * Inscreve um callback que dispara sempre que alguma tabela relevante para capacidade
   * muda no banco (tasks, task_assignments, resource_allocations, resources).
   *
   * Usado por PortfolioCapacityHub e TeamModule para refletir mudanças em <1s sem refresh.
   * Padrão copiado de AuthContext:148 (profile-block subscription).
   *
   * Retorna uma função de cleanup que deve ser chamada no useEffect return.
   * Debounce de 500ms no callback para evitar thrash em bursts (ex: drag de múltiplas tasks).
   */
  subscribeCapacityChanges(scopeKey: string, onChange: () => void): () => void {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const trigger = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        try { onChange(); } catch (err) { console.error('[capacityService.subscribeCapacityChanges] callback error:', err); }
      }, 500);
    };

    const channel = supabase
      .channel(`capacity-${scopeKey}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'resource_allocations' }, trigger)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, trigger)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_assignments' }, trigger)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'resources' }, trigger)
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }
};
